import React, { useEffect, useMemo, useState } from "react";
import TornaAlMenu from "../components/TornaAlMenu";

function headers() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("access")}`,
  };
}

function money(v) {
  return `€ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
}

function badgeClass(status) {
  if (status === "ACTIVE") return "text-bg-success";
  if (status === "USED") return "text-bg-secondary";
  if (status === "PENDING_PAYMENT") return "text-bg-warning";
  if (status === "CANCELLED") return "text-bg-danger";
  if (status === "EXPIRED") return "text-bg-dark";
  return "text-bg-light";
}

const defaultItem = { description: "Tagliere medio", quantity: 2, unit_price: "20.00" };

export default function GiftVouchersPage() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [verified, setVerified] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    voucher_type: "EXPERIENCE",
    buyer_name: "",
    buyer_email: "",
    recipient_name: "",
    dedication: "",
    title: "2 Taglieri Medi",
    total_amount: "50.00",
    items: [{ ...defaultItem }],
  });

  const totalExperience = useMemo(
    () =>
      (form.items || []).reduce(
        (sum, x) => sum + Number(x.quantity || 0) * Number(String(x.unit_price || 0).replace(",", ".")),
        0
      ),
    [form.items]
  );

  const fetchVouchers = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch("/api/gift-vouchers/", { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore caricamento buoni");
      setVouchers(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, []);

  const updateItem = (idx, key, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [key]: value };
    setForm({ ...form, items });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      voucher_type: "EXPERIENCE",
      buyer_name: "",
      buyer_email: "",
      recipient_name: "",
      dedication: "",
      title: "2 Taglieri Medi",
      total_amount: "50.00",
      items: [{ ...defaultItem }],
    });
  };

  const loadVoucherForEdit = (v) => {
    if (v.status === "USED") {
      setErr("Non puoi modificare un buono già utilizzato.");
      return;
    }
    if (v.sent_at) {
      setErr("Non puoi modificare un buono già inviato o scaricato definitivamente.");
      return;
    }

    setErr("");
    setOk("");
    setEditingId(v.id);
    setVerified(v);
    setVerifyCode(v.code || "");
    setForm({
      voucher_type: v.voucher_type || "EXPERIENCE",
      buyer_name: v.buyer_name || "",
      buyer_email: v.buyer_email || "",
      recipient_name: v.recipient_name || "",
      dedication: v.dedication || "",
      title: v.title || "",
      total_amount: v.total_amount || "0.00",
      items: (v.items || []).map((x) => ({
        description: x.description || "",
        quantity: x.quantity || 1,
        unit_price: x.unit_price || "0.00",
      })),
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const createVoucher = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");

    if (!form.buyer_name.trim()) {
      setErr("Inserisci il nome di chi acquista il buono.");
      return;
    }

    if (!form.buyer_email.trim()) {
      setErr("Inserisci l'email di chi acquista il buono.");
      return;
    }

    const firstItem = form.items?.[0];
    const autoTitle =
      form.voucher_type === "EXPERIENCE" && firstItem
        ? `${firstItem.quantity} x ${firstItem.description}`
        : `Gift card wallet da ${money(form.total_amount)}`;

    const payload = {
      ...form,
      title: form.title?.trim() ? form.title : autoTitle,
      total_amount: form.voucher_type === "WALLET" ? form.total_amount : String(totalExperience.toFixed(2)),
    };

    try {
      const url = editingId ? `/api/gift-vouchers/${editingId}/` : "/api/gift-vouchers/";
      const method = editingId ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: headers(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || (editingId ? "Errore modifica buono" : "Errore creazione buono"));

      setOk(editingId ? `Buono aggiornato: ${data.code}` : `Buono creato: ${data.code}`);
      setVerified(data);
      await fetchVouchers();
      resetForm();
    } catch (e2) {
      setErr(e2.message);
    }
  };

  const verifyVoucher = async () => {
    setErr("");
    setOk("");
    setVerified(null);
    try {
      const res = await fetch("/api/gift-vouchers/verify/", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ code: verifyCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Buono non valido");
      setVerified(data.voucher);
      setOk("Buono valido.");
    } catch (e) {
      setErr(e.message);
    }
  };

  const openPdf = async (id) => {
    setErr("");
    try {
      const res = await fetch(`/api/gift-vouchers/${id}/pdf/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
      });

      if (!res.ok) {
        throw new Error("Errore apertura PDF buono.");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      setErr(e.message);
    }
  };

  const redeemVoucher = async (code) => {
    if (!window.confirm("Confermi la convalida del buono? Dopo la convalida non sarà più utilizzabile.")) return;
    setErr("");
    setOk("");
    try {
      const res = await fetch("/api/gift-vouchers/redeem/", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore convalida buono");
      setVerified(data.voucher);
      setOk("Buono convalidato correttamente.");
      fetchVouchers();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="container py-4">
      <TornaAlMenu to="/gestione-app-clienti" />

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <div className="text-muted small text-uppercase fw-semibold">Gestione App Clienti</div>
          <h1 className="fw-bold mb-1">🎁 Buoni Regalo</h1>
          <div className="text-muted">Crea buoni esperienza, gift card wallet, verifica codici e convalida in negozio.</div>
        </div>
        <button className="btn btn-outline-secondary rounded-pill px-4" onClick={fetchVouchers}>🔄 Aggiorna</button>
      </div>

      {err && <div className="alert alert-danger rounded-4">{err}</div>}
      {ok && <div className="alert alert-success rounded-4">{ok}</div>}

      <div className="row g-4">
        <div className="col-12 col-xl-7">
          <div className="card border-0 shadow rounded-4 mb-4">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">{editingId ? "✏️ Modifica buono" : "➕ Crea buono"}</h4>

              <form onSubmit={createVoucher}>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Tipo buono</label>
                    <select className="form-select" value={form.voucher_type} onChange={(e) => setForm({ ...form, voucher_type: e.target.value })}>
                      <option value="EXPERIENCE">Buono esperienza</option>
                      <option value="WALLET">Gift card wallet</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Titolo</label>
                    <input className="form-control" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Acquirente</label>
                    <input required className="form-control" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Email acquirente</label>
                    <input required type="email" className="form-control" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Destinatario</label>
                    <input className="form-control" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Dedica</label>
                    <textarea className="form-control" rows="2" value={form.dedication} onChange={(e) => setForm({ ...form, dedication: e.target.value })} />
                  </div>

                  {form.voucher_type === "WALLET" ? (
                    <div className="col-12 col-md-6">
                      <label className="form-label">Importo gift card</label>
                      <input type="number" step="0.01" className="form-control" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} />
                    </div>
                  ) : (
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="fw-bold mb-0">Voci buono esperienza</h6>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: "0.00" }] })}>+ Aggiungi voce</button>
                      </div>

                      {(form.items || []).map((item, idx) => (
                        <div className="row g-2 align-items-end mb-2" key={idx}>
                          <div className="col-12 col-md-5">
                            <label className="form-label small">Descrizione</label>
                            <input className="form-control" value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} />
                          </div>
                          <div className="col-6 col-md-2">
                            <label className="form-label small">Quantità</label>
                            <input type="number" min="1" className="form-control" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
                          </div>
                          <div className="col-6 col-md-3">
                            <label className="form-label small">Prezzo unitario</label>
                            <input type="number" step="0.01" className="form-control" value={item.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} />
                          </div>
                          <div className="col-12 col-md-2">
                            <button type="button" className="btn btn-outline-danger w-100" disabled={form.items.length <= 1} onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}>Rimuovi</button>
                          </div>
                        </div>
                      ))}

                      <div className="alert alert-light border rounded-4 mt-3 mb-0">Totale esperienza: <strong>{money(totalExperience)}</strong></div>
                    </div>
                  )}

                  <div className="col-12">
                    <button className="btn btn-success rounded-pill px-4" type="submit">{editingId ? "Aggiorna buono" : "Genera buono manuale"}</button>
                    {editingId && (
                      <button type="button" className="btn btn-outline-secondary rounded-pill px-4 ms-2" onClick={resetForm}>
                        Annulla modifica
                      </button>
                    )}
                    <div className="form-text mt-2">Per ora il buono gestionale viene marcato come Attivo/Pagato manualmente. Stripe online arriverà nello step app.</div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card border-0 shadow rounded-4">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">✅ Verifica / Convalida buono</h4>
              <div className="input-group mb-3">
                <input className="form-control" value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} placeholder="Inserisci codice es. DZR-GIFT-XXXXXXXX" />
                <button className="btn btn-primary" type="button" onClick={verifyVoucher}>Verifica</button>
              </div>

              <button className="btn btn-outline-secondary rounded-pill" disabled>📷 Scansiona QR</button>
              <div className="form-text mb-3">Scanner QR in step successivo: useremo la fotocamera del telefono/tablet.</div>

              {verified && (
                <div className="border rounded-4 p-3">
                  <div className="d-flex justify-content-between gap-3">
                    <div>
                      <div className="fw-bold">{verified.title}</div>
                      <div className="text-muted small">{verified.code}</div>
                    </div>
                    <span className={`badge rounded-pill ${badgeClass(verified.status)}`}>{verified.status_label}</span>
                  </div>
                  <div className="mt-2"><strong>Totale:</strong> {money(verified.total_amount)}</div>
                  <ul className="mt-2 mb-3">
                    {(verified.items || []).map((x) => <li key={x.id}>{x.quantity} x {x.description} — {money(x.total_price)}</li>)}
                  </ul>
                  <div className="d-flex flex-wrap gap-2">
                    <button className="btn btn-outline-dark rounded-pill px-4" onClick={() => openPdf(verified.id)}>Scarica PDF</button>
                    {verified.status === "ACTIVE" && <button className="btn btn-success rounded-pill px-4" onClick={() => redeemVoucher(verified.code)}>Convalida buono</button>}
                    {verified.status !== "USED" && !verified.sent_at && (
                      <button className="btn btn-outline-primary rounded-pill px-4" onClick={() => loadVoucherForEdit(verified)}>
                        Modifica buono
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-5">
          <div className="card border-0 shadow rounded-4">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">📋 Elenco buoni</h4>
              {loading ? <p className="text-muted">Caricamento…</p> : vouchers.length === 0 ? (
                <div className="alert alert-info rounded-4">Nessun buono presente.</div>
              ) : (
                <div className="vstack gap-3">
                  {vouchers.map((v) => (
                    <div className="border rounded-4 p-3" key={v.id}>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <div className="fw-bold">{v.title}</div>
                          <div className="text-muted small">{v.code}</div>
                          <div className="small">{v.voucher_type_label}</div>
                        </div>
                        <span className={`badge rounded-pill ${badgeClass(v.status)}`}>{v.status_label}</span>
                      </div>
                      <div className="mt-2"><strong>{money(v.total_amount)}</strong></div>
                      {v.recipient_name && <div className="small text-muted">Destinatario: {v.recipient_name}</div>}
                      <div className="mt-2 d-flex gap-2">
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { setVerifyCode(v.code); setVerified(v); }}>Apri</button>
                        {v.status !== "USED" && !v.sent_at && (
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => loadVoucherForEdit(v)}>
                            Modifica
                          </button>
                        )}
                        <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => openPdf(v.id)}>PDF</button>
                        {v.status === "ACTIVE" && <button type="button" className="btn btn-sm btn-outline-success" onClick={() => redeemVoucher(v.code)}>Convalida</button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
